import React, { useEffect, useState } from 'react';
import { Repeat } from 'lucide-react';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Self-contained "Repeat" control — daily/weekly/monthly + end condition —
// shared by every compose flow that supports recurrence (Cross-Post,
// Instagram, LinkedIn). Calls onChange with the full recurrence object on
// every edit; the parent just reads the latest value at submit time.
export default function RecurrencePicker({ onChange, initial }) {
  const [frequency, setFrequency] = useState(initial?.frequency || 'none');
  const [interval, setInterval_] = useState(initial?.interval || 1);
  const [daysOfWeek, setDaysOfWeek] = useState(initial?.daysOfWeek || []);
  const [dayOfMonth, setDayOfMonth] = useState(initial?.dayOfMonth || 1);
  const [endType, setEndType] = useState(initial?.endType || 'never');
  const [endDate, setEndDate] = useState(initial?.endDate ? new Date(initial.endDate).toISOString().slice(0, 10) : '');
  const [occurrences, setOccurrences] = useState(initial?.occurrences || 10);

  useEffect(() => {
    onChange({
      frequency,
      interval,
      daysOfWeek: frequency === 'weekly' ? daysOfWeek : undefined,
      dayOfMonth: frequency === 'monthly' ? dayOfMonth : undefined,
      endType,
      endDate: endType === 'on' ? endDate : undefined,
      occurrences: endType === 'after' ? occurrences : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, interval, daysOfWeek, dayOfMonth, endType, endDate, occurrences]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-[13px] text-[#2E2E2E]">
        <Repeat size={14} className="text-[#5C5C5C] flex-shrink-0" />
        <span className="font-semibold">Repeat</span>
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          className="border border-[#E5E5E5] rounded-[4px] px-2 py-1 text-[13px] focus:outline-none focus:border-[#0176D3]"
        >
          <option value="none">Does not repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {frequency !== 'none' && (
        <div className="space-y-3 pl-5 border-l-2 border-[#E5E5E5]">
          <div className="flex items-center gap-2 text-[13px] text-[#2E2E2E]">
            <span>Every</span>
            <input
              type="number"
              min={1}
              value={interval}
              onChange={(e) => setInterval_(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-14 border border-[#E5E5E5] rounded-[4px] px-2 py-1 text-[13px] text-center focus:outline-none focus:border-[#0176D3]"
            />
            <span>{{ daily: 'day(s)', weekly: 'week(s)', monthly: 'month(s)' }[frequency]}</span>
          </div>

          {frequency === 'weekly' && (
            <div className="flex items-center gap-1">
              {WEEKDAY_LABELS.map((label, idx) => {
                const active = daysOfWeek.includes(idx);
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() =>
                      setDaysOfWeek((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()))
                    }
                    className={`w-8 h-8 rounded-full text-[11px] font-semibold border ${
                      active
                        ? 'bg-[#0176D3] text-white border-[#0176D3]'
                        : 'bg-white text-[#5C5C5C] border-[#E5E5E5] hover:border-[#0176D3]'
                    }`}
                  >
                    {label[0]}
                  </button>
                );
              })}
            </div>
          )}

          {frequency === 'monthly' && (
            <div className="flex items-center gap-2 text-[13px] text-[#2E2E2E]">
              <span>On day</span>
              <input
                type="number"
                min={1}
                max={31}
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                className="w-14 border border-[#E5E5E5] rounded-[4px] px-2 py-1 text-[13px] text-center focus:outline-none focus:border-[#0176D3]"
              />
              <span className="text-[11px] text-[#5C5C5C]">of the month</span>
            </div>
          )}

          <div className="space-y-2 text-[13px] text-[#2E2E2E]">
            <div className="font-semibold text-[11px] text-[#5C5C5C]">Ends</div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={endType === 'never'} onChange={() => setEndType('never')} className="accent-[#0176D3]" />
              Never
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={endType === 'on'} onChange={() => setEndType('on')} className="accent-[#0176D3]" />
              On
              {endType === 'on' && (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)}
                  className="border border-[#E5E5E5] rounded-[4px] px-2 py-1 text-[13px] focus:outline-none focus:border-[#0176D3]"
                />
              )}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" checked={endType === 'after'} onChange={() => setEndType('after')} className="accent-[#0176D3]" />
              After
              {endType === 'after' && (
                <>
                  <input
                    type="number"
                    min={1}
                    value={occurrences}
                    onChange={(e) => setOccurrences(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-14 border border-[#E5E5E5] rounded-[4px] px-2 py-1 text-[13px] text-center focus:outline-none focus:border-[#0176D3]"
                  />
                  occurrences
                </>
              )}
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
